/**
 * Photo weight (lead's design 10/08: no proxy, no rehosting, no database change): ask the photo's own host for a smaller
 * rendition it already serves, by host. Anything not on this list is loaded as stored (lazily, low priority, on cards).
 * Every rule is unit-tested (tests/image_size_test.ts) and the rewritten URLs were checked on live samples (HEAD 200).
 * A rewritten URL that fails falls back to the stored one before the card shows its symbol tile (components/browse/Photo).
 */
export type ImageUse = 'card' | 'detail'

// Ticketmaster's named 16:9 renditions; the card uses RETINA_PORTRAIT_16_9 (640x360, ~24 KB vs 64-210 KB)
const TM_16_9 = ['RETINA_LANDSCAPE_16_9', 'TABLET_LANDSCAPE_16_9', 'TABLET_LANDSCAPE_LARGE_16_9', 'RETINA_PORTRAIT_16_9', 'EVENT_DETAIL_PAGE_16_9']
const TM_RE = new RegExp(`^(https://s1\\.ticketm\\.net/.+_)(${TM_16_9.join('|')})(\\.jpg)$`)

export function sized(url: string, use: ImageUse): string {
  if (use === 'detail') {
    // the page's larger photo: stored size, except the hosts where the stored one is far bigger than 800 px needs
    return url
  }
  let u: URL
  try { u = new URL(url) } catch { return url }
  switch (u.hostname) {
    case 's1.ticketm.net': {
      const m = url.match(TM_RE)
      return m ? `${m[1]}RETINA_PORTRAIT_16_9${m[3]}` : url
    }
    case 'commons.wikimedia.org': {
      if (!u.pathname.startsWith('/wiki/Special:FilePath/')) return url
      u.searchParams.set('width', '480')
      return u.toString()
    }
    case 'images.lumacdn.com': {
      // .../cdn-cgi/image/<options>/<path>: options are comma-separated key=value
      return url.replace(/(\/cdn-cgi\/image\/[^/]*?)width=\d+([^/]*?)height=\d+/, '$1width=480$2height=252')
    }
    case 'cdn.tech-week.com':
      return url.replace(/-800\.webp$/, '-400.webp')
    case 'www.eventbrite.com':
      // their image route only serves its own widths: 640 answers 400; 384 and 1080 answer 200 (checked 10/08)
      return u.pathname.endsWith('/_next/image') ? url.replace(/&w=\d+/, '&w=384') : url
    default:
      return url
  }
}

/** True when the card loads a smaller rendition than the stored photo (the rest get fetchpriority=low). */
export const hasSmaller = (url: string) => sized(url, 'card') !== url
